import React, { useState, useRef, useEffect } from 'react';
import { HiOutlineEye, HiOutlineEyeOff } from 'react-icons/hi';

/**
 * 비밀번호 입력 필드 컴포넌트
 * - 입력 중에는 마지막 글자를 실제 문자로 표시
 * - 다른 필드로 포커스가 이동하면 모든 글자를 '*'로 표시
 * - 눈 아이콘을 클릭하여 비밀번호를 완전히 평문으로 보거나 숨길 수 있음
 */
const PasswordInput = ({
  value = '',
  onChange,
  placeholder = '비밀번호를 입력하세요',
  className = '',
  disabled = false,
  maxLength,
  onBlur,
  onFocus,
  ...props
}) => {
  const [displayValue, setDisplayValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const inputRef = useRef(null);

  // value가 변경될 때 displayValue 업데이트
  useEffect(() => {
    if (showPassword) {
      // 비밀번호 표시 모드: 모든 글자를 실제 문자로 표시
      setDisplayValue(value);
    } else {
      // 비밀번호 숨김 모드
      if (!isFocused) {
        // 포커스가 없을 때는 모두 '*'로 표시
        setDisplayValue('*'.repeat(value.length));
      } else {
        // 포커스가 있을 때는 마지막 글자만 실제 문자로 표시
        if (value.length > 0) {
          const masked = '*'.repeat(value.length - 1);
          const lastChar = value[value.length - 1];
          setDisplayValue(masked + lastChar);
        } else {
          setDisplayValue('');
        }
      }
    }
  }, [value, isFocused, showPassword]);

  const handleFocus = (e) => {
    setIsFocused(true);
    if (onFocus) {
      onFocus(e);
    }
  };

  const handleBlur = (e) => {
    setIsFocused(false);
    if (onBlur) {
      onBlur(e);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();

    // 클립보드에서 붙여넣기된 텍스트 가져오기
    const pastedText = (e.clipboardData || window.clipboardData).getData('text');

    if (!pastedText) {
      return;
    }

    // 붙여넣기된 텍스트를 직접 value에 설정
    const syntheticEvent = {
      ...e,
      target: {
        ...e.target,
        value: pastedText
      }
    };

    if (onChange) {
      onChange(syntheticEvent);
    }
  };

  const handleChange = (e) => {
    const inputValue = e.target.value;

    // showPassword가 true일 때는 직접 입력된 값을 사용
    if (showPassword) {
      if (onChange) {
        onChange(e);
      }
      return;
    }

    // showPassword가 false일 때는 마스킹된 값에서 실제 값을 추출
    const currentLength = value.length;
    const inputLength = inputValue.length;

    // 입력 길이가 증가한 경우 (새로운 글자 입력)
    if (inputLength > currentLength) {
      // 새로 입력된 글자는 마지막에 추가된 실제 문자
      const newChar = inputValue[inputLength - 1];
      const newValue = value + newChar;

      // 이벤트 객체를 수정하여 새로운 값을 전달
      const syntheticEvent = {
        ...e,
        target: {
          ...e.target,
          value: newValue
        }
      };

      if (onChange) {
        onChange(syntheticEvent);
      }
    }
    // 입력 길이가 감소한 경우 (삭제)
    else if (inputLength < currentLength) {
      // 삭제된 만큼 실제 값에서도 제거
      const newValue = value.substring(0, inputLength);

      const syntheticEvent = {
        ...e,
        target: {
          ...e.target,
          value: newValue
        }
      };

      if (onChange) {
        onChange(syntheticEvent);
      }
    }
    // 입력 길이가 같은 경우 (편집, 선택 후 입력 등)
    else {
      // 마스킹된 값에서 실제 값을 복원
      // displayValue는 "***a" 형태 (마지막 글자만 실제 문자)이거나 "****" 형태
      // 사용자가 선택 후 새로 입력한 경우를 처리
      const lastChar = inputValue[inputLength - 1];
      const maskedPart = inputValue.substring(0, inputLength - 1);
      const isAllMasked = maskedPart.length === 0 || maskedPart.split('').every(c => c === '*');

      if (isAllMasked && lastChar && lastChar !== '*') {
        // 마지막 글자가 실제 문자이고 나머지가 모두 '*'인 경우
        // 이는 새로 입력한 것으로 간주
        const syntheticEvent = {
          ...e,
          target: {
            ...e.target,
            value: lastChar
          }
        };

        if (onChange) {
          onChange(syntheticEvent);
        }
      } else if (inputLength === 0) {
        // 모두 삭제된 경우
        const syntheticEvent = {
          ...e,
          target: {
            ...e.target,
            value: ''
          }
        };

        if (onChange) {
          onChange(syntheticEvent);
        }
      }
      // 그 외의 경우는 현재 value 유지 (편집 방지)
    }
  };

  const toggleShowPassword = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowPassword(!showPassword);
  };

  // 실제 input은 숨기고, 표시용 input을 사용
  return (
    <div className="relative">
      <input
        {...props}
        ref={inputRef}
        type="text"
        value={displayValue}
        onChange={handleChange}
        onPaste={handlePaste}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        className={`${className} pr-10`}
        disabled={disabled}
        maxLength={maxLength}
        autoComplete="off"
      />
      {/* 비밀번호 표시/숨김 토글 버튼 */}
      <button
        type="button"
        onClick={toggleShowPassword}
        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none disabled:opacity-50"
        disabled={disabled}
        tabIndex={-1}
        aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 표시하기'}
      >
        {showPassword ? (
          <HiOutlineEyeOff className="w-5 h-5" />
        ) : (
          <HiOutlineEye className="w-5 h-5" />
        )}
      </button>
      {/* 실제 비밀번호 입력을 위한 숨겨진 input (보안을 위해) */}
      <input
        type="password"
        value={value}
        onChange={onChange}
        style={{
          position: 'absolute',
          opacity: 0,
          pointerEvents: 'none',
          width: '1px',
          height: '1px',
        }}
        tabIndex={-1}
        autoComplete="new-password"
      />
    </div>
  );
};

export default PasswordInput;
